/**
 * CSV export utilities for downloading tabular data.
 */

type CellValue = string | number | boolean | null | undefined;

/**
 * Escapes a cell value for safe CSV inclusion.
 * Wraps in quotes and escapes internal quotes.
 */
const escapeCell = (value: CellValue): string => {
    const str = (value ?? '').toString();
    return `"${str.replace(/"/g, '""')}"`;
};

/**
 * Downloads an array of data as a CSV file.
 * 
 * @param headers - Column header names
 * @param rows - Array of row arrays, each containing cell values
 * @param filename - Name for the downloaded file (should end in .csv)
 */
export const downloadCsv = (
    headers: string[],
    rows: CellValue[][],
    filename: string
): void => {
    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(escapeCell).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Generates a timestamped filename for exports.
 * 
 * @param prefix - Prefix for the filename (e.g., "tunnels_export")
 * @returns Filename like "tunnels_export_2024-01-15.csv"
 */
export const generateExportFilename = (prefix: string): string => {
    const date = new Date().toISOString().split('T')[0];
    return `${prefix}_${date}.csv`;
};
